import React from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { Button, Label, TextInput, Select } from 'flowbite-react';
import { v4 as uuidv4 } from 'uuid';
import { useDatabase } from '../../hooks/useDatabase';

const NativeResourceGroupSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  managedByTeamId: z.string().optional(),
});

type Values = z.infer<typeof NativeResourceGroupSchema>;

interface Props {
  onSubmit: (command: any) => Promise<void>;
  onClose: () => void;
  isOpen: boolean;
  onCreated?: (id: string) => void;
  defaultName?: string;
  defaultManagedByTeamId?: string;
}

const NativeResourceGroupForm: React.FC<Props> = ({
  onSubmit,
  onClose,
  isOpen,
  onCreated,
  defaultName,
  defaultManagedByTeamId,
}) => {
  if (!isOpen) return null;
  const { database } = useDatabase();
  const teams = database
    ? Object.entries(database.teams()).map(([id, t]: [string, any]) => ({ id, name: t.name() }))
    : [];

  const handleSubmit = async (values: Values) => {
    const id = uuidv4();
    const command = {
      type: 'CreateItem',
      id,
      item: {
        type: 'ResourceGroup',
        value: {
          name: values.name,
          managed_by_team: values.managedByTeamId ? values.managedByTeamId : null,
        },
      },
    };
    await onSubmit(command);
    onCreated?.(id);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-cm-bg-surface p-6 rounded-lg shadow-lg w-full max-w-md">
        <h2 className="text-xl font-semibold mb-4 text-cm-text-primary">Create Resource Group</h2>

        <Formik
          initialValues={{
            name: defaultName ?? '',
            managedByTeamId: defaultManagedByTeamId ?? '',
          }}
          validationSchema={toFormikValidationSchema(NativeResourceGroupSchema)}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="space-y-4">
              <div>
                <div className="mb-2">
                  <Label htmlFor="name">Name</Label>
                </div>
                <Field as={TextInput} id="name" name="name" autoFocus placeholder="e.g. Microscopes, Conference Rooms" />
                <ErrorMessage name="name" component="div" className="text-red-500 text-sm mt-1" />
              </div>

              <div>
                <div className="mb-2">
                  <Label htmlFor="managedByTeamId">Managed by Team</Label>
                </div>
                <Field as={Select} id="managedByTeamId" name="managedByTeamId">
                  <option value="">— none —</option>
                  {teams.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </Field>
              </div>

              <div className="flex justify-end space-x-2 mt-6">
                <Button color="gray" onClick={onClose}>Cancel</Button>
                <Button type="submit" disabled={isSubmitting}>Create Group</Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default NativeResourceGroupForm;
